import React, {useState, useEffect, useContext} from 'react';
import { Table, TableHead, TableRow, TableCell, TableBody, Checkbox, Typography, IconButton, Menu, MenuItem,
    TableContainer, Paper, Tooltip, Box } from '@mui/material';
import { MoreVert, Edit, Delete, ContentCopy } from '@mui/icons-material';
import {Link} from 'react-router-dom';
import {AlertContext} from "../App/App";
import {colorLink, colorText} from "../App/theme";
import {formatScore} from "../utils";
import {estimateItemTokens} from "../Chat/utils";
import {decryptKBItemIfEncrypted, getItemImageUrl} from "../App/cryptoUtils";
import {deleteItem} from "../API/items/itemsAPI";
import {useConfirm} from "../Components/ConfirmContext";

const ItemsTable = React.memo(({
    embeddingConfigured, items, selectMode, selectedItems,
    devMode, kbId, itemsIndexedDB, itemsVectorDB, setSelectedItems, KBData, setItems
}) => {
    const [renderedItemsCount, setRenderedItemsCount] = useState(20); // Initial number of items to render
    const {systemAlert, setSystemAlert} = useContext(AlertContext);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);

    const showConfirm = useConfirm();
    const handleMenuClick = (event, itemId) => {
        setAnchorEl(event.currentTarget);
        setSelectedItem(itemId);
    };

    const onDelete = async (kbId, itemId, itemType) => {
        try {
            await deleteItem({itemId, KBData, itemType});
            if (items) setItems(items.filter((item) => item.meta.itemId !== itemId));
        } catch (error) {
            if (error?.response?.data?.error) alert(error.response.data.error);
            console.error('Error deleting kb:', error);
        }
    };

    const handleDelete = async (kbId, itemId, itemType) => {
        const confirmed = await showConfirm({
            title: 'Delete Item Confirmation',
            content:  `Are you sure you want to delete item ${itemId} ?`
        })

        if (confirmed) {
            onDelete(kbId, itemId, itemType);
        }
    };

    const handleSelect = (itemId) => {
        if (selectedItems.includes(itemId)) {
            setSelectedItems(selectedItems.filter(id => id !== itemId));
        } else {
            setSelectedItems([...selectedItems, itemId]);
        }
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleScroll = () => {
        const bottom = Math.ceil(window.innerHeight + window.scrollY) >= document.documentElement.scrollHeight;
        if (bottom) {
            const length = itemsVectorDB?.length || itemsIndexedDB?.length || items?.length
            setRenderedItemsCount(prevCount => {
                return Math.min(prevCount + 20, length) || 20
            });
        }
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [items, itemsIndexedDB, itemsVectorDB]);

    const handleCopy = (itemId) => {
        setSystemAlert({msg: 'Copied to clipboard', type: 'success', duration: 1500})
        navigator.clipboard.writeText(itemId);
    };

    const CopyButton = (value) => <IconButton size="small" onClick={() => handleCopy(value)} sx={{ ml: 0 }}>
            <ContentCopy fontSize="small" sx={{fontSize: 12}}/>
        </IconButton>

    const myItems = itemsVectorDB || itemsIndexedDB || items

    if (!embeddingConfigured || !myItems) return null;

    // Extract all unique attribute names from all items
    const attributeNames = Array.from(new Set(myItems?.flatMap(item => item?.meta?.attributes?.filter(attr => attr.attrType !== 'itemId').map(attr => attr.attrName))));

    const colWidth = 50;

    return (
        <>
            <TableContainer component={Paper}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {selectMode && <TableCell />}
                            <TableCell sx={{ maxWidth: colWidth, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>itemId</TableCell>
                            <TableCell sx={{ maxWidth: colWidth, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Type</TableCell>
                            {attributeNames.map(attrName => (
                                <TableCell key={attrName} sx={{ maxWidth: colWidth, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{attrName}</TableCell>
                            ))}
                            <TableCell sx={{ maxWidth: 100, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>tokens</TableCell>
                            {/*{devMode && <TableCell sx={{ maxWidth: 100, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Score</TableCell>}*/}
                            {myItems?.[0]?.meta?.score && <TableCell sx={{ maxWidth: colWidth, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>score</TableCell>}
                            {!itemsIndexedDB && !itemsVectorDB && <TableCell sx={{ maxWidth: colWidth, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>actions</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {myItems.slice(0, renderedItemsCount).map(({ item, meta }) => {
                            let imageItem = getItemImageUrl({item, meta});

                            return (
                                <TableRow key={meta.itemId}>
                                    {selectMode && (
                                        <TableCell>
                                            <Checkbox
                                                edge="start"
                                                checked={selectedItems.includes(meta.itemId)}
                                                tabIndex={-1}
                                                disableRipple
                                                onChange={() => handleSelect(meta.itemId)}
                                            />
                                        </TableCell>
                                    )}

                                    <TableCell
                                        sx={{ color: colorText, cursor: 'pointer', maxWidth: colWidth, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                    >
                                        <Box display="flex" alignItems="center" className="id-container">
                                            <Tooltip
                                                title={
                                                    <table>
                                                        <tbody>
                                                        <tr>
                                                            <td style={{ paddingRight: '10px', fontWeight: 'bold' }}>
                                                                {CopyButton(meta.itemId)} itemId
                                                            </td>
                                                            <td style={{ wordBreak: 'break-word' }}>{meta.itemId}</td>
                                                        </tr>
                                                            <tr>
                                                                <td style={{ paddingRight: '10px', fontWeight: 'bold' }}>
                                                                    {CopyButton(meta.updatedAt)} Updated:
                                                                </td>
                                                                <td>{new Date(parseInt(meta.updatedAt)).toLocaleString()}</td>
                                                            </tr>
                                                        <tr>
                                                            <td style={{ paddingRight: '10px', fontWeight: 'bold' }}>
                                                                {CopyButton(meta.createdAt)} Created:
                                                            </td>
                                                            <td>{new Date(parseInt(meta.createdAt)).toLocaleString()}</td>
                                                        </tr>
                                                        </tbody>
                                                    </table>
                                                }
                                                arrow
                                                placement={"top-start"}
                                            >
                                                <Typography
                                                    style={{ color: colorLink, fontSize: '0.8rem', }}
                                                    component={Link}
                                                    to={`/edit-item/${meta.itemId}?returnTo=database`}
                                                    variant="body1"
                                                >
                                                    {meta.itemId}
                                                </Typography>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>

                                    <TableCell
                                        sx={{ color: colorText, maxWidth: colWidth, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                    >
                                        <Typography variant="body1">
                                            {meta.itemType}
                                        </Typography>
                                    </TableCell>
                                    {attributeNames.map(attrName => (
                                        <TableCell key={attrName} sx={{ maxWidth: colWidth, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            <Typography variant="body2">
                                                {meta?.attributes?.find(attr => attr.attrName === attrName)?.encrypted
                                                    ? decryptKBItemIfEncrypted(item[attrName])
                                                    : (item[attrName] === true && 'True' || item[attrName]) || ''}

                                            </Typography>

                                            {imageItem && attrName === imageItem.imageUrlAttributeName && imageItem.imageUrl && <img width={64} src={imageItem.imageUrl} />}
                                        </TableCell>
                                    ))}
                                    <TableCell sx={{ maxWidth: 100, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        <Typography variant="body2">
                                            {meta?.totalTokens}
                                        </Typography>
                                    </TableCell>
                                    {myItems[0].meta.score && (
                                        <>
                                            <TableCell sx={{ maxWidth: colWidth, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                <Typography variant="body2">
                                                    {meta.score === 1 ? 100 : formatScore(meta.score)}
                                                </Typography>
                                            </TableCell>
                                        </>
                                    )}
                                    {!itemsIndexedDB && !itemsVectorDB && <TableCell sx={{ maxWidth: colWidth, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        <IconButton edge="end" aria-label="menu"
                                                    onClick={(e) => handleMenuClick(e, meta.itemId)}
                                        >
                                            <MoreVert />
                                        </IconButton>
                                        <Menu
                                            anchorEl={anchorEl}
                                            open={Boolean(anchorEl) && selectedItem === meta.itemId}
                                            onClose={handleMenuClose}
                                        >
                                            <MenuItem
                                                component={Link}
                                                to={`/edit-item/${meta.itemId}?returnTo=database`}
                                                onClick={handleMenuClose}
                                            >
                                                <Edit fontSize="small" sx={{ mr: '12px' }} />Edit
                                            </MenuItem>
                                            <MenuItem
                                                onClick={() => {
                                                    handleDelete(kbId, meta.itemId, meta.itemType);
                                                    handleMenuClose();
                                                }}
                                            >
                                                <Delete fontSize="small" sx={{ mr: '12px' }} />Delete
                                            </MenuItem>
                                        </Menu>
                                    </TableCell>}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <style>
                {`
                    .id-container:hover .copy-icon {
                        display: inline-flex;
                    }
                    .copy-icon {
                        display: none;
                    }
                `}
            </style>
        </>
    );
})

export default ItemsTable;